import { api } from "@repo/convex/api";
import type { Doc, Id } from "@repo/convex/dataModel";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { useMemo } from "react";

/** One row of the project lookup, named where it is built rather than restated here. */
type NotificationProjectLookup = FunctionReturnType<typeof api.projects.listLookupByIds>[number];

// ============================================================================
// Notifications Feature - Data Hooks
// ============================================================================
// Hooks for fetching notification data from Convex backend.
// These power the notifications bell badge and popover panel.

/**
 * Get the count of unread notifications for the current user.
 * Used by NotificationBell to show/hide the badge.
 *
 * Returns a real-time count that updates automatically via Convex subscription.
 */
export function useUnreadNotificationCount(organizationId: Id<"orgs"> | null) {
  return useQuery(
    api.notifications.getUnreadCount,
    organizationId ? { orgId: organizationId } : "skip"
  );
}

/**
 * Fetch recent notifications for the current user.
 * Used by the notification popover to display the list.
 *
 * Returns notifications in reverse chronological order (newest first).
 * Default limit is 20 (set by the backend).
 */
export function useNotifications(
  organizationId: Id<"orgs"> | null,
  options: number | { limit?: number; enabled?: boolean } = {}
) {
  const limit = typeof options === "number" ? options : options.limit;
  const enabled = typeof options === "number" ? true : (options.enabled ?? true);

  return useQuery(
    api.notifications.listRecent,
    organizationId && enabled ? { orgId: organizationId, limit } : "skip"
  );
}

/**
 * Fetch minimal project display/navigation data for projects referenced by
 * loaded notifications. Skips entirely until the popover is open.
 */
export function useNotificationProjectLookups(
  organizationId: Id<"orgs"> | null,
  notifications: Doc<"notifications">[] | undefined,
  enabled: boolean
) {
  const projectIds = useMemo(() => {
    if (!notifications) return [];

    return Array.from(
      new Set(
        notifications
          .map((notification) => notification.projectId)
          .filter((projectId): projectId is Id<"projects"> => projectId !== undefined)
      )
    );
  }, [notifications]);

  const projects = useQuery(
    api.projects.listLookupByIds,
    organizationId && enabled && projectIds.length > 0
      ? { orgId: organizationId, projectIds }
      : "skip"
  );

  return useMemo(() => {
    if (projectIds.length === 0) {
      return new Map<Id<"projects">, NotificationProjectLookup>();
    }
    if (projects === undefined) {
      return undefined;
    }
    return new Map(projects.map((project) => [project._id, project]));
  }, [projectIds, projects]);
}
